import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, OnInit, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { StaffData } from '@interfaces';
import { AppService } from '../../../../services';
import { StaffSelectorWidget } from '../../staff-selector';

export type AssignedStaffDialogData = {
	headerText: string;
	staffId: number;
};

export interface AssignedStaffDialogResult {
	staffId: number | undefined,
}

@Component({
	selector: 'programs-assigned-staff-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		FormsModule,
		StaffSelectorWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './assigned-staff.dialog.html'
})

export class AssignedStaffDialog implements OnInit {

	public dialogRef = inject(MatDialogRef<AssignedStaffDialog>);
	public dialogContext: DialogContext<AssignedStaffDialogData> = inject(MAT_DIALOG_DATA);

	public readonly app = inject(AppService);

	@Output() staffChanged = new EventEmitter<number | undefined>();

	public readonly headerText = computed(() => {
		return this.dialogContext.data?.headerText || 'Assign Staff';
	});

	public readonly selectedStaff = signal<{ staffId?: number, personId?: number, fullName?: string }>({});
	public readonly originalSelectedStaff = signal<{ staffId?: number, personId?: number, fullName?: string }>({});
	public staffData = signal<StaffData[]>([]);

	public readonly hasChanges = computed(() => {
		const current = this.selectedStaff();
		const original = this.originalSelectedStaff();
		return current.staffId !== original.staffId;
	});

	public actions = computed<DialogAction[]>(() => {
		return [
			{
				id: 'close',
				cssClass: signal('btn-standard'),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Close'),
				linkType: "no-action",
				willCloseDialog: true,
			},
			{
				id: 'remove',
				cssClass: signal('btn-danger'),
				enabled: signal(!!this.originalSelectedStaff().staffId), // Enable only if current staffId exists
				visible: signal(true),
				text: signal('Remove'),
				linkType: "callback",
				willCloseDialog: false,
				callback: async () => await this.removeStaffAssignment()
			},
			{
				id: 'save',
				cssClass: signal('btn-success'),
				enabled: signal(this.hasChanges()), // Enable only if user made a change
				visible: signal(true),
				text: signal('Save'),
				linkType: "callback",
				willCloseDialog: false,
				callback: async () => await this.saveStaffAssignment()
			}
		];
	});

	async ngOnInit() {
		const data = this.dialogContext.data;
		const initialStaffId = data?.staffId ?? undefined;

		// Find the initial staff object from staffData if we have an ID
		this.staffData.set(this.app.data().staff);

		if (initialStaffId) {
			const initialStaff = this.staffData().find(staff => staff.staffId === initialStaffId);
			if (initialStaff) {
				const staffObj = {
					staffId: initialStaff.staffId,
					personId: initialStaff.personId,
					fullName: initialStaff.fullName
				};
				this.selectedStaff.set(staffObj);
				this.originalSelectedStaff.set(staffObj);
			}
		}
	}

	handleStaffValueChange(value: { staffId?: number, personId?: number, fullName?: string } | undefined) {
		if (value) {
			this.selectedStaff.set(value);
		} else {
			this.selectedStaff.set({});
		}
	}

	private async removeStaffAssignment() {
		// Set staff to empty object and emit undefined
		this.selectedStaff.set({});
		this.staffChanged.emit(undefined);

		// Close dialog with remove result (pass null to indicate explicit removal)
		this.closeDialog(null);
	}

	private async saveStaffAssignment() {
		// Emit the current selected staff ID
		const currentStaff = this.selectedStaff();
		this.staffChanged.emit(currentStaff.staffId);

		// Close dialog with success result
		this.closeDialog(currentStaff.staffId);
	}

	public async closeDialog(staffId?: number | null) {
		// Handle both defined staffId (including null for remove)
		if (arguments.length > 0) {
			let action = this.actions().find(a => a.id === 'save' || a.id === 'remove')!;
			action = {
				...action,
				callbackResult: <AssignedStaffDialogResult>{
					staffId: staffId === null ? undefined : staffId
				}
			};
			this.dialogRef.close(action);
		} else {
			this.dialogRef.close();
		}
	}
}